/**
 * Fullscreen viewer for the socials (UGC) carousel.
 *
 * The cards render small on purpose, so the full clip opens in an overlay on
 * click. Standalone on purpose: the socials-section element lives in global.js,
 * which ships minified as global.min.js, and editing one side of that pair
 * changes nothing on the storefront.
 *
 * The full-resolution source is read off the card, so the overlay never has to
 * reach into the videojs player the card is running.
 *
 * The clips are silent by policy, so the overlay is muted and has no control
 * bar: it is the card's loop, larger. A tap anywhere outside the clip closes it.
 */
(() => {
  if (window.__socialsVideoViewer) return;

  const MEDIA_SELECTOR = '.socials-section__media[data-ugc-full-src]';
  const DRAG_SLOP = 10;
  const FADE_MS = 200;

  let overlay = null;
  let overlayVideo = null;
  let closeButton = null;
  let openerMedia = null;
  let lastFocused = null;
  let pointerStart = null;
  let hideTimer = null;
  let spinner = null;

  const stopSpinner = () => {
    if (spinner) spinner.hidden = true;
  };

  const cardVideo = (media) => media?.querySelector('video') || null;

  function build() {
    if (overlay) return;

    overlay = document.createElement('div');
    overlay.className = 'socials-viewer';
    overlay.hidden = true;
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');

    const frame = document.createElement('div');
    frame.className = 'socials-viewer__frame';

    overlayVideo = document.createElement('video');
    overlayVideo.className = 'socials-viewer__video';
    overlayVideo.setAttribute('playsinline', '');
    overlayVideo.muted = true;
    overlayVideo.loop = true;
    overlayVideo.preload = 'auto';

    spinner = document.createElement('div');
    spinner.className = 'socials-viewer__spinner';
    spinner.hidden = true;

    closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'socials-viewer__close';
    closeButton.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">' +
      '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M6 18 18 6M6 6l12 12"/>' +
      '</svg>';

    frame.append(overlayVideo, spinner, closeButton);
    overlay.append(frame);
    document.body.append(overlay);

    // The clip is only "loading" until it can actually play. canplay covers a
    // cached file that never fires waiting, playing covers a cold one.
    overlayVideo.addEventListener('canplay', stopSpinner);
    overlayVideo.addEventListener('playing', stopSpinner);
    overlayVideo.addEventListener('error', stopSpinner);
    overlayVideo.addEventListener('waiting', () => {
      if (!overlay.hidden) spinner.hidden = false;
    });

    closeButton.addEventListener('click', close);
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay || event.target === frame) close();
    });
  }

  function open(media) {
    const src = media.dataset.ugcFullSrc;
    if (!src) return;

    build();
    clearTimeout(hideTimer);

    openerMedia = media;
    lastFocused = document.activeElement;

    const inline = cardVideo(media);
    if (inline) inline.pause();

    overlay.setAttribute('aria-label', media.dataset.ugcFullLabel || 'Video');
    closeButton.setAttribute('aria-label', media.dataset.ugcCloseLabel || 'Close');

    const poster = media.dataset.ugcFullPoster;
    overlayVideo.poster = poster || '';
    // Shape the box before any video data arrives, so the player does not open
    // at the 300x150 default and snap to portrait a moment later.
    overlay.style.setProperty('--socials-viewer-ratio', media.dataset.ugcFullRatio || '0.5625');

    const isSameClip = overlayVideo.getAttribute('src') === src;
    if (isSameClip) {
      // Reopening the same card starts it over. Only safe once metadata exists,
      // which it does by definition on a second open.
      try {
        overlayVideo.currentTime = 0;
      } catch {
        /* seek refused before metadata; it plays from wherever it is */
      }
    } else {
      overlayVideo.setAttribute('src', src);
    }
    spinner.hidden = overlayVideo.readyState >= 3;

    overlay.hidden = false;
    document.body.classList.add('scrollLock');
    // Two frames: the first paints the overlay, the second lets the fade run.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => overlay.classList.add('is-open'));
    });

    overlayVideo.play().catch(() => {});

    closeButton.focus({ preventScroll: true });
    document.addEventListener('keydown', onKeydown);
  }

  function close() {
    if (!overlay || overlay.hidden) return;

    overlay.classList.remove('is-open');
    overlayVideo.pause();
    stopSpinner();
    document.body.classList.remove('scrollLock');
    document.removeEventListener('keydown', onKeydown);

    hideTimer = setTimeout(() => {
      overlay.hidden = true;
    }, FADE_MS);

    const inline = cardVideo(openerMedia);
    if (inline) inline.play()?.catch(() => {});

    if (lastFocused && document.contains(lastFocused)) {
      lastFocused.focus({ preventScroll: true });
    }
    openerMedia = null;
    lastFocused = null;
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    // The close button is the only thing to focus inside the overlay, so the
    // trap is to send Tab straight back to it.
    if (event.key === 'Tab') {
      event.preventDefault();
      closeButton.focus({ preventScroll: true });
    }
  }

  // A swipe through the carousel ends in a click on the card it landed on, so a
  // click that travelled is treated as a drag and ignored.
  document.addEventListener(
    'pointerdown',
    (event) => {
      pointerStart = event.target.closest?.(MEDIA_SELECTOR)
        ? { x: event.clientX, y: event.clientY }
        : null;
    },
    true
  );

  document.addEventListener('click', (event) => {
    const media = event.target.closest?.(MEDIA_SELECTOR);
    if (!media) return;

    if (pointerStart) {
      const moved =
        Math.abs(event.clientX - pointerStart.x) + Math.abs(event.clientY - pointerStart.y);
      pointerStart = null;
      if (moved > DRAG_SLOP) return;
    }

    event.preventDefault();
    open(media);
  });

  window.__socialsVideoViewer = { open, close };
})();
